import { useState } from 'react';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeInput } from '@/components/ui/tee-input';
import TeeBadge from '@/components/ui/tee-badge';
import { useOnboardGamePlan } from '@/hooks/useGamePlanTracker';
import type { CurrentWeekResponse } from '@/types/gamePlanTracker';

const DAY = 86400000;

interface OnboardingWizardProps {
  locationId: string;
  defaultGoal: number;
  defaultDate: string; // ISO yyyy-mm-dd
  onComplete: () => void;
}

type Step = 'goal' | 'date' | 'deck';

function daysBetweenToday(iso: string): number {
  if (!iso) return -1;
  const [y, m, d] = iso.split('-').map(Number);
  const launch = Date.UTC(y, m - 1, d);
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((launch - today) / DAY);
}

export function OnboardingWizard({
  locationId,
  defaultGoal,
  defaultDate,
  onComplete,
}: OnboardingWizardProps) {
  const [step, setStep] = useState<Step>('goal');
  const [goal, setGoal] = useState(defaultGoal || 50);
  const [date, setDate] = useState(defaultDate);
  const [built, setBuilt] = useState<CurrentWeekResponse | null>(null);
  const [deckIndex, setDeckIndex] = useState(0);

  const onboard = useOnboardGamePlan(locationId);

  const daysToGo = daysBetweenToday(date);
  const dateInvalid = !date || daysToGo < 7;
  const isCompressed = !dateInvalid && daysToGo < 112;
  const weeksToGo = Math.max(0, Math.round(daysToGo / 7));
  const dateError = !date
    ? 'Pick your Grand Opening date.'
    : daysToGo < 7
    ? 'Pick a date at least a week from today.'
    : '';

  const build = async () => {
    const res = await onboard.mutateAsync({
      foundingMemberGoal: goal,
      grandOpeningDate: date,
      confirmCompressed: isCompressed,
    });
    setBuilt(res);
    setDeckIndex(res.plan.startIndex);
    setStep('deck');
  };

  return (
    <div className="max-w-[640px] mx-auto">
      {step === 'goal' && (
        <div className="section_body flex flex-col gap-6 items-center text-center py-10">
          <TeeBadge label="Step 1 of 2" type="grey" size="s" />
          <h2 className="text-heading-h3 text-[var(--text-content-title)]">
            How many Founding Members are you launching with?
          </h2>
          <p className="text-body-m text-[var(--text-content-sub-title)]">
            We recommend 50. You can adjust in groups of 5.
          </p>
          <div className="flex items-center gap-6">
            <TeeButton
              variant="secondary"
              size="l"
              text="–"
              onClick={() => setGoal((g) => Math.max(50, g - 5))}
              disabled={goal <= 50}
            />
            <span className="text-[48px] font-semibold text-[var(--color-forest)] w-[120px]">
              {goal}
            </span>
            <TeeButton
              variant="secondary"
              size="l"
              text="+"
              onClick={() => setGoal((g) => Math.min(150, g + 5))}
              disabled={goal >= 150}
            />
          </div>
          <TeeButton
            variant="primary"
            size="l"
            text="Continue"
            onClick={() => setStep('date')}
          />
        </div>
      )}

      {step === 'date' && (
        <div className="section_body flex flex-col gap-6 py-10">
          <div className="flex flex-col items-center gap-3 text-center">
            <TeeBadge label="Step 2 of 2" type="grey" size="s" />
            <h2 className="text-heading-h3 text-[var(--text-content-title)]">
              When is your Grand Opening?
            </h2>
            <p className="text-body-m text-[var(--text-content-sub-title)]">
              Your 16-week plan is anchored to this date.
            </p>
          </div>
          <TeeInput
            label="Grand Opening date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            destructive={!!dateError}
            hint={dateError}
          />

          {isCompressed && (
            <div className="rounded-[10px] border border-[var(--border-default)] bg-[var(--background-page-header)] p-4 flex flex-col gap-2">
              <p className="text-body-m font-medium text-[var(--text-content-title)]">
                Condensed plan
              </p>
              <p className="text-body-s text-[var(--text-content-sub-title)]">
                ~{weeksToGo} weeks to Grand Opening · same goal of {goal}, higher
                weekly pace. We’ll start you in the week you’re in today.
              </p>
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <TeeButton
              variant="tertiary"
              size="m"
              text="Back"
              onClick={() => setStep('goal')}
            />
            <TeeButton
              variant="primary"
              size="l"
              text={isCompressed ? 'Confirm condensed plan' : 'Build my Game Plan'}
              onClick={build}
              disabled={dateInvalid || onboard.isPending}
            />
          </div>
        </div>
      )}

      {step === 'deck' && built && (
        <DeckView
          res={built}
          index={deckIndex}
          onIndex={setDeckIndex}
          onStart={onComplete}
        />
      )}
    </div>
  );
}

function DeckView({
  res,
  index,
  onIndex,
  onStart,
}: {
  res: CurrentWeekResponse;
  index: number;
  onIndex: (i: number) => void;
  onStart: () => void;
}) {
  const phases = res.plan.phases;
  const phase = phases[index];
  return (
    <div className="section_body flex flex-col gap-5 py-8">
      <div className="text-center flex flex-col gap-1">
        <h2 className="text-heading-h3 text-[var(--text-content-title)]">
          {res.goal} Founding Members by {res.plan.launchLabel}
        </h2>
        <p className="text-body-s text-[var(--text-content-sub-title)]">
          {res.plan.startLine}
        </p>
      </div>

      <div className="rounded-[12px] border border-[var(--border-default)] bg-[var(--background-card)] p-5 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-heading-h5 text-[var(--text-content-title)]">
              {phase.name}
            </p>
            <p className="text-body-s text-[var(--text-content-sub-title)]">
              {phase.label} · {phase.dateRange}
            </p>
          </div>
          <TeeBadge
            label={`Target: ${phase.targetMembers} members`}
            type="green"
            size="s"
          />
        </div>
        <ol className="flex flex-col gap-2">
          {phase.bullets.map((b, i) => (
            <li
              key={i}
              className="text-body-s text-[var(--text-content-title)] flex gap-2"
            >
              <span className="text-[var(--color-text-gold)]">{i + 1}.</span>
              <span>{b}</span>
            </li>
          ))}
        </ol>
      </div>

      <div className="flex items-center justify-between">
        <TeeButton
          variant="secondary"
          size="m"
          text="Previous"
          onClick={() => onIndex(Math.max(0, index - 1))}
          disabled={index === 0}
        />
        <div className="flex gap-1.5">
          {phases.map((p, i) => (
            <span
              key={p.key}
              className={
                'h-2 w-2 rounded-full ' +
                (i === index
                  ? 'bg-[var(--color-forest)]'
                  : 'bg-[var(--border-default)]')
              }
            />
          ))}
        </div>
        {index < phases.length - 1 ? (
          <TeeButton
            variant="secondary"
            size="m"
            text="Next"
            onClick={() => onIndex(Math.min(phases.length - 1, index + 1))}
          />
        ) : (
          <TeeButton
            variant="primary"
            size="m"
            text="See your Game Plan →"
            onClick={onStart}
          />
        )}
      </div>
    </div>
  );
}
